import React, { useState, useEffect } from 'react';
import { trackEvent } from '../../../utils/posthogTracker';

const ONBOARDING_STORAGE_KEY = 'ai_text_editor_onboarding_completed';

const AITextEditorOnboarding = ({ 
  runOnboarding, 
  hasCompletedOnboarding,
  onOnboardingComplete, 
  onOnboardingSkip
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Mark onboarding as completed in localStorage
  const markOnboardingCompleted = () => {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    } catch (error) {
      console.warn('Could not save onboarding state:', error);
    }
  };

  useEffect(() => {
    if (runOnboarding && !hasCompletedOnboarding) {
      setIsVisible(true);
      setCurrentStep(0);
      
      // Track onboarding start
      trackEvent('ai_text_editor_onboarding_started', {
        component: 'AITextEditor',
        is_returning_user: hasCompletedOnboarding,
        timestamp: new Date().toISOString()
      });
    }
  }, [runOnboarding, hasCompletedOnboarding]);

  const steps = [
    {
      title: "¡Bienvenido al Editor de Texto con IA!",
      content: (
        <div>
          <p>
            Este editor te permite mejorar tu texto usando inteligencia artificial. 
            Te mostraré cómo usar todas sus funciones paso a paso.
          </p>
        </div>
      )
    },
    {
      title: "Paso 1: Selecciona texto",
      content: (
        <div>
          <p>
            Para usar las funciones de IA, primero escribe texto en el editor 
            y luego <strong>selecciona</strong> la parte que quieres mejorar.
          </p>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '10px',
            borderRadius: '4px',
            marginTop: '10px',
            border: '1px solid #dee2e6'
          }}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              <strong>💡 Ejemplo:</strong> Cuando escribas texto y lo selecciones, 
              aparecerá un menú con diferentes herramientas de IA para mejorar tu texto.
            </p>
          </div>
          <p style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
            <em>Tip: Puedes seleccionar una palabra, frase o párrafo completo.</em>
          </p>
        </div>
      )
    },
    {
      title: "Paso 2: Menú flotante de IA",
      content: (
        <div>
          <p>
            Cuando selecciones texto, aparecerá un menú flotante con opciones de IA:
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            marginTop: '10px'
          }}>
            <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <strong>✨ Mejora escritura:</strong> Hace el texto más claro y profesional
            </div>
            <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <strong>✓ Corrige gramática:</strong> Corrige errores gramaticales
            </div>
            <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <strong>📝 Hacer más corto:</strong> Reduce la longitud del texto
            </div>
            <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <strong>📖 Hacer más largo:</strong> Expande el texto con más detalles
            </div>
            <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', gridColumn: '1 / -1' }}>
              <strong>➡️ Continúa escritura:</strong> Completa el texto automáticamente
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Paso 3: Sugerencias de IA",
      content: (
        <div>
          <p>
            Una vez que selecciones una opción, la IA procesará tu texto y te mostrará 
            una sugerencia. Verás:
          </p>
          <div style={{
            backgroundColor: '#fff3cd',
            padding: '10px',
            borderRadius: '4px',
            marginTop: '10px',
            border: '1px solid #ffeaa7'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ 
                width: '16px', 
                height: '16px', 
                backgroundColor: '#ff6b6b',
                borderRadius: '50%',
                marginRight: '8px'
              }}></div>
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Propuesta de IA pendiente</span>
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
              El texto sugerido reemplazará temporalmente tu texto original
            </p>
          </div>
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '10px'
          }}>
            <span style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '12px',
              display: 'inline-block'
            }}>
              ✓ Aceptar
            </span>
            <span style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '12px',
              display: 'inline-block'
            }}>
              ✗ Rechazar
            </span>
          </div>
        </div>
      )
    },
    {
      title: "Paso 4: Preguntar a la IA",
      content: (
        <div>
          <p>
            Además de las opciones del menú, también puedes hacer preguntas directas a la IA 
            usando el campo "Preguntar a la IA..." que aparece debajo del editor.
          </p>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '4px',
            marginTop: '10px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              padding: '8px 12px',
              fontSize: '14px',
              color: '#666'
            }}>
              <span style={{ flex: 1 }}>Preguntar a la IA...</span>
              <span style={{ 
                backgroundColor: '#0fa69a',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px'
              }}>➤</span>
            </div>
          </div>
          <div style={{
            backgroundColor: '#e7f3ff',
            padding: '10px',
            borderRadius: '4px',
            marginTop: '10px',
            border: '1px solid #b3d9ff'
          }}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              <strong>💡 Ejemplos de preguntas:</strong> "¿Esta idea de startup es viable?", 
              "¿Cómo puedo hacer más atractiva mi propuesta?", "¿Qué le falta a mi modelo de negocio?"
            </p>
          </div>
        </div>
      )
    },
    {
      title: "🎉 ¡Listo para empezar!",
      content: (
        <div>
          <p>
            Ahora ya sabes cómo usar el Editor de Texto con IA. Recuerda el proceso:
          </p>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '4px',
            marginTop: '10px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px',
                  marginRight: '8px'
                }}>1</span>
                <span style={{ fontSize: '14px' }}>Escribe tu texto</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px',
                  marginRight: '8px'
                }}>2</span>
                <span style={{ fontSize: '14px' }}>Selecciona lo que quieres mejorar</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px',
                  marginRight: '8px'
                }}>3</span>
                <span style={{ fontSize: '14px' }}>Elige una opción del menú flotante</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px',
                  marginRight: '8px'
                }}>4</span>
                <span style={{ fontSize: '14px' }}>Acepta o rechaza según te convenga</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px',
                  marginRight: '8px'
                }}>5</span>
                <span style={{ fontSize: '14px' }}>Usa "Preguntar a la IA" para consultas directas</span>
              </div>
            </div>
          </div>
          <div style={{
            backgroundColor: '#d4edda',
            padding: '12px',
            borderRadius: '4px',
            marginTop: '10px',
            border: '1px solid #c3e6cb',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
              🚀 ¡Experimenta y mejora tu escritura con IA!
            </p>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      
      // Track step progression
      trackEvent('ai_text_editor_onboarding_step_completed', {
        component: 'AITextEditor',
        step_index: currentStep,
        step_title: steps[currentStep]?.title || 'unknown',
        timestamp: new Date().toISOString()
      });
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    
    // Mark as completed in localStorage
    markOnboardingCompleted();
    
    // Track onboarding completion
    trackEvent('ai_text_editor_onboarding_completed', {
      component: 'AITextEditor',
      total_steps: steps.length,
      completed_steps: steps.length,
      timestamp: new Date().toISOString()
    });
    
    if (onOnboardingComplete) {
      onOnboardingComplete();
    }
  };

  const handleSkip = () => {
    setIsVisible(false);
    
    // Mark as completed in localStorage even if skipped
    markOnboardingCompleted();
    
    // Track onboarding skip
    trackEvent('ai_text_editor_onboarding_skipped', {
      component: 'AITextEditor',
      total_steps: steps.length,
      completed_steps: currentStep,
      skipped_at_step: currentStep,
      timestamp: new Date().toISOString()
    });
    
    if (onOnboardingSkip) {
      onOnboardingSkip();
    }
  };

  const handleClose = () => {
    handleSkip();
  };

  if (!isVisible) {
    return null;
  }

  const currentStepData = steps[currentStep];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            color: '#666',
            width: '30px',
            height: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%'
          }}
        >
          ×
        </button>

        {/* Progress indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <div style={{
            backgroundColor: '#f0f0f0',
            borderRadius: '10px',
            padding: '4px 12px',
            fontSize: '12px',
            color: '#666'
          }}>
            {currentStep + 1} de {steps.length}
          </div>
        </div>

        {/* Step content */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            margin: '0 0 16px 0',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            {currentStepData.title}
          </h4>
          <div style={{
            fontSize: '14px',
            lineHeight: '1.5',
            color: '#666'
          }}>
            {currentStepData.content}
          </div>
        </div>

        {/* Navigation buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <button
              onClick={handleSkip}
              style={{
                background: 'none',
                border: '1px solid #ddd',
                color: '#666',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Omitir
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Atrás
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {currentStep === steps.length - 1 ? 'Finalizar' : 'Siguiente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITextEditorOnboarding;